import {
    getFilterOptionsFromClassifier,
    getSortedFilterOptions,
    mapToFilterOptions,
} from 'components/tables/list-table/filterUtil.ts';
import { DynamicMultiselectFilterForm, FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import { useMemo } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinInvoicesListWithFiltersQuery } from 'store/api/invoices.ts';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetPracticeAreasWithFiltersQuery } from 'store/api/practice-areas.ts';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { PracticeAreaType } from 'types/practice-areas.ts';
import { TimecardStatusType } from 'types/timecards';
import {
    enrichDynamicFiltersWithPassiveFilter,
    filterMainFilters,
    PaginatedFieldPassiveFilter,
} from 'utils/filterUtil.ts';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { ASSIGNMENT_KEYWORDS_JOINT } from './assignmentKeywordsFilter';

const filterStatusTypes = [
    TimecardStatusType.DRAFT,
    TimecardStatusType.FINISHED,
    TimecardStatusType.DRAFT_INVOICE,
    TimecardStatusType.INVOICED,
    TimecardStatusType.PAID,
];

/** What each status means in invoicing terms; the FINISHED status is labelled "Uninvoiced" on the list. */
const TIMECARD_STATUS_TOOLTIPS: Partial<Record<string, string>> = {
    [TimecardStatusType.FINISHED]: 'timecards.tooltips.status-uninvoiced',
    [TimecardStatusType.DRAFT_INVOICE]: 'timecards.tooltips.status-draft-invoice',
    [TimecardStatusType.INVOICED]: 'timecards.tooltips.status-invoiced',
    [TimecardStatusType.PAID]: 'timecards.tooltips.status-paid',
};

/** The classifiers the timecards list reads, its filters included. */
export const TIMECARD_LIST_CLASSIFIERS = [
    'TIMECARD_STATUS_TYPE',
    'LANGUAGE',
    'CURRENCY',
    'TIMECARD_ACTIVITY_TYPE',
    'TIMECARD_VALUE_FACTORS',
    // The Invoice column's tooltip names each invoice's status.
    'INVOICE_STATUS_TYPE',
];

// Add more if needed
export type FilterId = 'matterIdInFilter';
export type FilterGroup = 'timecards.group.case' | 'timecards.group.client';

type Args = {
    userId?: number;
    disableMainFilters?: boolean;
    passiveFilters?: string;
    classifiers: Record<string, ClassifierData>;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
    /**
     * The draft tab lists the person's own drafts alone, or everyone's under READ.TIMECARDS.DRAFT.ALL; any other value
     * is the main list, which hides drafts.
     */
    tabType?: 'main' | 'draft';
};

/**
 * The timecards list's filters: its quick filters, the fields of its filter panel, what its search reads and the
 * filter it always applies. One definition for the list and for whatever else filters timecards by the same fields.
 */
export function useTimecardFilterConfig({
    userId,
    disableMainFilters = false,
    passiveFilters = '',
    classifiers,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    tabType,
}: Args): FilterConfig {
    const isBilledTimeControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_BILLED_TIME_CONTROL);
    const canReadAllDrafts = useHasPermissions(['READ.TIMECARDS.DRAFT.ALL']);

    const timecardStatusTypeOptions = useMemo(
        () => mapToFilterOptions(filterStatusTypes, classifiers['TIMECARD_STATUS_TYPE']),
        [classifiers]
    );

    // Filter out DRAFT status from filters for MAIN tab (both quick and main filters)
    const filteredStatusOptions = useMemo(() => {
        if (tabType === 'main') {
            return timecardStatusTypeOptions.filter((option) => option.value !== TimecardStatusType.DRAFT);
        }
        return timecardStatusTypeOptions;
    }, [tabType, timecardStatusTypeOptions]);

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                // Hidden on the DRAFT tab, which lists the user's own drafts, or everyone's under
                // READ.TIMECARDS.DRAFT.ALL, where the author filter narrows them
                ...(tabType !== 'draft'
                    ? [
                          {
                              selector: 'id',
                              comperator: '=ismynondrafttimecard=',
                              filters: [
                                  {
                                      label: t('timecards.filter.my-timecards'),
                                      argument: 'true',
                                  },
                              ],
                          },
                          {
                              selector: 'id',
                              comperator: '=ismyteamtimecard=',
                              filters: [
                                  {
                                      label: t('timecards.filter.my-team-timecards'),
                                      argument: 'true',
                                      tooltip: 'timecards.tooltips.filters-timecards-author-team-matches-my-team',
                                  },
                              ],
                          },
                      ]
                    : []),
                // Only on a list that spans clients: under a single case/client the client group is
                // disabled and the shortcut would filter the whole list in or out. `==` keeps its key
                // off the main filter's `:=in=` one — a shared key hands the clause to the main
                // control, which lists the opposite argument and would swallow this one.
                ...(disabledGroups?.includes('timecards.group.client')
                    ? []
                    : [
                          {
                              selector: 'matter.clientParty.isInternalClient',
                              comperator: '==',
                              filters: [
                                  {
                                      label: t('timecards.filter.internal-clients'),
                                      argument: 'true',
                                  },
                              ],
                          },
                      ]),
                // Hide status filters for DRAFT tab since it only shows draft timecards
                ...(tabType !== 'draft'
                    ? [
                          {
                              selector: 'timecardStatusTypeCode',
                              chipLabel: 'filters.status',
                              multiple: true,
                              filters: filteredStatusOptions,
                          },
                      ]
                    : []),
                {
                    selector: 'isNonBillable',
                    chipLabel: 'timecards.filter.billable',
                    filters: [
                        { label: t('timecards.form.is-non-billable'), argument: 'true' },
                        { label: t('timecards.form.billable'), argument: 'false' },
                    ],
                },
            ],
            filters: disableMainFilters
                ? []
                : enrichDynamicFiltersWithPassiveFilter(
                      filterMainFilters(
                          [
                              // -------- CLIENT --------
                              {
                                  type: 'dynamicmultiselect',
                                  subType: 'contact',
                                  label: t('timecards.filter.client-name'),
                                  selector: 'matter.clientParty.id',
                                  options: getSortedFilterOptions('name', 'id', []),
                                  group: 'timecards.group.client',
                                  paginated: {
                                      fetch: useLazyGetMinContactsListWithFiltersQuery,
                                      searchField: ['name', 'partyNumber'],
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'name',
                                  },
                              } as MainFilter,
                              // -------- CASE --------
                              {
                                  id: 'matterIdInFilter',
                                  type: 'dynamicmultiselect',
                                  label: t('timecards.filter.case-name'),
                                  selector: 'matter.id',
                                  group: 'timecards.group.case',
                                  jointFilter: ASSIGNMENT_KEYWORDS_JOINT,
                                  getOptionStartAdornment: renderMatterIndicators,
                                  paginated: {
                                      fetch: useLazyGetMattersListQuery,
                                      searchField: ['fullName', 'clientParty.name'],
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'fullName',
                                      groupByField: 'clientParty.name',
                                      sort: ['clientParty.name,asc', 'name,asc'],
                                      getFilter: (_, mf) => {
                                          const filter = mf.find(
                                              (filter) => filter.selector === 'matter.clientParty.id'
                                          ) as DynamicMultiselectFilterForm | undefined;
                                          const selectedClientIds = (
                                              filter?.selected
                                                  ? Array.isArray(filter?.selected)
                                                      ? filter.selected
                                                      : [filter.selected]
                                                  : []
                                          ).map(({ value }) => value);
                                          if (selectedClientIds.length === 0) {
                                              return undefined;
                                          }
                                          return `clientParty.id=in=(${selectedClientIds.join(',')})`;
                                      },
                                  },
                              },
                              {
                                  type: 'text',
                                  selector: ASSIGNMENT_KEYWORDS_JOINT.joinSelector,
                                  label: t('timecards.filter.assignment-name'),
                                  group: 'timecards.group.case',
                                  multiple: true,
                                  virtual: true,
                                  tooltip: 'timecards.tooltips.searches-keywords-within-filtered-cases',
                                  getShouldHide: ({ mainFilters }) => {
                                      const matterFilter = Object.entries(mainFilters).find(
                                          ([key, value]) =>
                                              key.startsWith('matter.id:') && value.type === 'dynamicmultiselect'
                                      )?.[1] as DynamicMultiselectFilterForm;
                                      return matterFilter.selected?.length === 0;
                                  },
                              },

                              // -------- TIMECARD --------
                              {
                                  type: 'daterange',
                                  label: t('timecards.filter.timecard-date'),
                                  shouldDisplayQuickPick: true,
                                  selector: 'timecardDate',
                                  group: 'timecards.group.timecard',
                              },
                              // Hidden on the DRAFT tab, like the quick filters above
                              ...(tabType !== 'draft'
                                  ? [
                                        {
                                            type: 'checkboxgroup' as const,
                                            selector: 'id',
                                            comperator: '=ismynondrafttimecard=',
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            filters: [
                                                {
                                                    label: t('timecards.filter.my-timecards'),
                                                    argument: 'true',
                                                },
                                            ],
                                        },
                                        {
                                            type: 'checkboxgroup' as const,
                                            selector: 'id',
                                            comperator: '=ismyteamtimecard=',
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            filters: [
                                                {
                                                    label: t('timecards.filter.my-team-timecards'),
                                                    argument: 'true',
                                                    tooltip:
                                                        'timecards.tooltips.filters-timecards-author-team-matches-my-team',
                                                },
                                            ],
                                        },
                                    ]
                                  : []),
                              {
                                  type: 'checkboxgroup',
                                  selector: 'matter.clientParty.isInternalClient',
                                  group: 'timecards.group.timecard',
                                  filters: [
                                      {
                                          label: t('timecards.filter.exclude-internal-clients'),
                                          argument: 'false',
                                      },
                                  ],
                              },
                              {
                                  type: 'dynamicmultiselect',
                                  label: t('timecards.filter.timecard-author'),
                                  selector: 'employee.id',
                                  group: 'timecards.group.timecard',
                                  paginated: {
                                      fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                      searchField: 'fullName',
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'name',
                                  },
                              },
                              {
                                  type: 'dynamicmultiselect',
                                  label: t('timecards.colleagues-office'),
                                  selector: 'employee.currentEmployment.office.id',
                                  tooltip: 'timecards.tooltips.filters-timecards-author-matches-offices',
                                  group: 'timecards.group.timecard',
                                  paginated: {
                                      fetch: useLazyGetMinOfficesWithFiltersQuery,
                                      searchField: 'name',
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'name',
                                  },
                                  getOptionEndAdornment: officeInactiveSuffix,
                              },
                              {
                                  type: 'dynamicmultiselect',
                                  label: t('timecards.colleagues-team'),
                                  selector: 'employee.currentEmployment.coreTeam.id',
                                  tooltip: 'timecards.tooltips.filters-timecards-author-matches-teams',
                                  group: 'timecards.group.timecard',
                                  paginated: {
                                      fetch: useLazyGetMinTeamsListWithFiltersQuery,
                                      searchField: 'name',
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'name',
                                  },
                              },
                              // Hide status filter for DRAFT tab since it only shows draft timecards
                              ...(tabType !== 'draft'
                                  ? [
                                        {
                                            type: 'checkboxgroup' as const,
                                            selector: 'timecardStatusTypeCode',
                                            label: t('timecards.filter.timecard-status'),
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            filters: filteredStatusOptions.map((option) => ({
                                                ...option,
                                                tooltip: TIMECARD_STATUS_TOOLTIPS[option.value],
                                            })),
                                        },
                                    ]
                                  : []),
                              {
                                  type: 'text',
                                  selector: 'description',
                                  label: t('timecards.filter.timecard-content-search'),
                                  group: 'timecards.group.timecard',
                                  tooltip: 'timecards.tooltips.search-timecards-details-of-work-done',
                                  multiple: true,
                              },
                              {
                                  type: 'multiselect',
                                  selector: 'languageTypeCode',
                                  label: t('timecards.filter.timecard-language'),
                                  group: 'timecards.group.timecard',
                                  options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['LANGUAGE']) : [],
                              },
                              {
                                  type: 'dynamicmultiselect',
                                  selector: 'timecardPracticeAreas.practiceArea.id',
                                  label: t('timecards.filter.practice-area'),
                                  group: 'timecards.group.timecard',
                                  paginated: {
                                      fetch: useLazyGetPracticeAreasWithFiltersQuery,
                                      searchField: 'name',
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'name',
                                      filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                                  },
                              },
                              {
                                  type: 'dynamicmultiselect',
                                  selector: 'invoiceTimecards.invoice.id',
                                  label: t('timecards.filter.invoice-number'),
                                  group: 'timecards.group.timecard',
                                  paginated: {
                                      fetch: useLazyGetMinInvoicesListWithFiltersQuery,
                                      searchField: 'invoiceNumber',
                                      getOptionIdentifier: 'id',
                                      getOptionLabel: 'invoiceNumber',
                                  },
                              },
                              {
                                  type: 'checkboxgroup',
                                  selector: 'isNonBillable',
                                  label: t('timecards.filter.show'),
                                  group: 'timecards.group.timecard',
                                  multiple: true,
                                  filters: [
                                      { label: t('timecards.form.is-non-billable'), argument: 'true' },
                                      { label: t('timecards.form.billable'), argument: 'false' },
                                  ],
                              },
                              ...(tabType !== 'draft'
                                  ? [
                                        {
                                            type: 'checkboxgroup' as const,
                                            selector: 'id',
                                            comperator: '=isuninvoicedforclosing=',
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            filters: [
                                                {
                                                    label: t('timecards.filter.blocks-case-closing'),
                                                    argument: 'true',
                                                    tooltip: 'timecards.tooltips.blocks-case-closing',
                                                },
                                            ],
                                        },
                                    ]
                                  : []),
                              // Both uplift filters ride custom comparators rather than a column: "billed
                              // exceeds used" compares two columns, and the factors live in a jsonb array.
                              ...(isBilledTimeControlEnabled
                                  ? [
                                        {
                                            type: 'checkboxgroup' as const,
                                            selector: 'id',
                                            comperator: '=billinguplift=',
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            // On the checkbox, not the group: CheckboxGroup renders the
                                            // group tooltip only inside `{filter.label && …}`, and this
                                            // group is deliberately label-less (one self-describing box).
                                            filters: [
                                                {
                                                    label: t('timecards.filter.billing-uplift'),
                                                    argument: 'true',
                                                    tooltip: 'timecards.tooltips.billing-uplift',
                                                },
                                            ],
                                        },
                                        {
                                            type: 'multiselect' as const,
                                            selector: 'billingUpliftValueFactors',
                                            comperator: '=valuefactor=',
                                            label: t('timecards.filter.billing-uplift-value-factors'),
                                            group: 'timecards.group.timecard' as FilterGroup,
                                            tooltip: 'timecards.tooltips.billing-uplift-value-factors',
                                            options: getFilterOptionsFromClassifier(
                                                classifiers?.['TIMECARD_VALUE_FACTORS']
                                            ),
                                        },
                                    ]
                                  : []),
                          ],
                          disabledGroups,
                          disabledFilterIds
                      ),
                      paginatedFieldPassiveFilters
                  ),
            searchBarSelectors: [
                { selector: 'matter.name' },
                { selector: 'matter.caseNumber' },
                { selector: 'employee.fullName' },
                { selector: 'matter.clientParty.name' },
                { selector: 'description' },
                { selector: 'matterAssignment.name' },
            ],
            passiveFilter:
                tabType === 'draft'
                    ? `timecardStatusTypeCode==DRAFT${
                          canReadAllDrafts ? '' : `;createdByEmployee.id==(${userId?.toString() ?? ''})`
                      }${passiveFilters.length ? ';' + passiveFilters : ''}`
                    : `timecardStatusTypeCode=out=(DRAFT)${passiveFilters.length ? ';' + passiveFilters : ''}`,
        }),
        [
            userId,
            canReadAllDrafts,
            disableMainFilters,
            passiveFilters,
            classifiers,
            disabledGroups,
            disabledFilterIds,
            paginatedFieldPassiveFilters,
            tabType,
            filteredStatusOptions,
            isBilledTimeControlEnabled,
        ]
    );

    return filterConfig;
}
